import { useState, useEffect } from 'react';
import { Card, CardTitle } from '@/components/ui/card';
import { WindowTabs, type BriefWindow } from '@/components/common/WindowTabs';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHero, KpiStat } from '@/components/common/PageHero';
import { DigitalSummary } from '@/components/digital/DigitalSummary';
import { DigitalGroup } from '@/components/digital/DigitalGroup';
import { SourceChips } from '@/components/digital/SourceChips';
import { useDigitalSummary, useDigitalItems } from '@/lib/queries';
import { cn } from '@/lib/utils';

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

const labelClass = 'text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)';

const windowDays: Record<BriefWindow, number> = { '24h': 1, '7d': 7, '30d': 30 };

export default function Digital() {
  const [window, setWindow] = useState<BriefWindow>('7d');
  const [days, setDays] = useState(7);
  const [minScore, setMinScore] = useState(3);
  const [source, setSource] = useState('all');

  // Sync days when window changes, but allow user to override afterwards.
  useEffect(() => { setDays(windowDays[window]); }, [window]);

  const summary = useDigitalSummary(window);
  const items = useDigitalItems({ days, minScore, source });

  const totalItems = items.data?.groups.reduce((sum, g) => sum + g.items.length, 0) ?? 0;
  const windowLabel = window === '24h' ? 'Last 24 hours' : window === '7d' ? 'Last 7 days' : 'Last 30 days';

  return (
    <div>
      <PageHero
        eyebrow="Digital"
        title={<>Ecosystem <span className="text-muted font-normal">in motion</span></>}
        lede="What competitors, customers, partners, and regulators are doing right now in drilling-digital. Filter by source, time, and minimum score."
        side={<WindowTabs value={window} onChange={setWindow} />}
        stats={
          items.data && (
            <dl className="flex gap-8 md:gap-12 flex-wrap">
              <KpiStat label="Items" value={totalItems} tone="accent" />
              <KpiStat label="Categories" value={items.data.groups.length} tone="accent" />
              <KpiStat label="Window" value={windowLabel} />
            </dl>
          )
        }
      />

      {summary.isLoading && <LoadingSkeleton lines={4} />}
      {summary.error && <ErrorState message={(summary.error as Error).message} />}
      {summary.data && <DigitalSummary summary={summary.data} />}

      <Card>
        <CardTitle className="text-xl">Recent activity by stakeholder</CardTitle>
        <p className="text-muted text-sm mt-1">
          Last {days} days · score ≥ {minScore} · themes: competitor, customer, partner, regulation, technology
        </p>
        <div className="flex flex-wrap gap-3 mt-3 items-end">
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Range</span>
            <select className={selectClass} value={days} data-focus-filter onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Min score</span>
            <select className={selectClass} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))}>
              <option value={3}>score ≥ 3</option>
              <option value={5}>score ≥ 5</option>
              <option value={7}>score ≥ 7</option>
            </select>
          </label>
        </div>
        {items.data && (
          <SourceChips chips={items.data.chips} value={source} onChange={setSource} />
        )}
      </Card>

      {items.isLoading && <LoadingSkeleton lines={6} />}
      {items.error && <ErrorState message={(items.error as Error).message} />}

      {items.data && items.data.groups.length === 0 && (
        <Card>
          <p className="text-muted">
            No digital items in this window yet. Try widening the time range or lowering min score.
          </p>
        </Card>
      )}

      {items.data && items.data.groups.map((g) => (
        <DigitalGroup key={g.category} group={g} />
      ))}
    </div>
  );
}
